import { withErrorContext } from ".";
import * as Sentry from "@sentry/browser";
// @ts-expect-error TS(7016)
import isUrl from "is-url";
import { dropWhile, flatten } from "lodash";
import { Descendant, Editor, Element, Node, Text, Transforms } from "slate";
import { jsx } from "slate-hyperscript";

import { trackEngagement } from "helpers/analytics";
import { normalizeBrowserLink } from "utils/string";

import { RichEditorText, TextTagFunction } from "../types";

import { EditorImageType } from "./images";
import { ONELINE_FORMATS } from "./onelineHeaders";
import { BLOCK_FORMATS, RichTextFormatType } from "./richText";

const ELEMENT_TAGS = {
  A: (
    el: HTMLAnchorElement
  ): {
    type?: string;
    url?: string;
  } => {
    const href = el.getAttribute("href") || undefined;
    const result = normalizeBrowserLink(href);
    return result.success ? { type: "link", url: result.url.trim() } : {};
  },
  H1: (): { type: string } => ({ type: "heading-one" }),
  H2: (): { type: string } => ({ type: "heading-two" }),
  H3: (): { type: string } => ({ type: "heading-three" }),
  H4: (): { type: string } => ({ type: "heading-four" }),
  H5: (): { type: string } => ({ type: "heading-five" }),
  H6: (): { type: string } => ({ type: "heading-six" }),
  LI: (): { type: string } => ({ type: "list-item" }),
  OL: (): { type: string } => ({ type: "numbered-list" }),
  P: (): { type: string } => ({ type: "paragraph" }),
  BLOCKQUOTE: (): { type: string } => ({ type: "paragraph" }),
  UL: (): { type: string } => ({ type: "bulleted-list" }),
  IMG: (
    el: HTMLImageElement
  ): {
    type: string;
    imageType: EditorImageType;
    alt: string;
    url: string;
  } => ({
    type: "image",
    imageType: EditorImageType.ExternalImage,
    alt: el.alt,
    url: el.src,
  }),
};

const TEXT_TAGS: Record<string, TextTagFunction> = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
  B: () => ({ bold: true }),
  SPAN: (el: HTMLSpanElement) => {
    const marks: { [key in RichTextFormatType]?: boolean } = {};

    if (Number.isNaN(parseInt(el.style.fontWeight, 10))) {
      marks.bold = ["bold", "bolder"].includes(el.style.fontWeight);
    } else {
      marks.bold = parseInt(el.style.fontWeight, 10) > 400;
    }

    if (marks.bold === false) {
      delete marks.bold;
    }

    if (el.style.textDecoration.toLowerCase() === "underline") {
      marks.underline = true;
    }

    if (el.style.textDecoration.toLowerCase() === "line-through") {
      marks.strikethrough = true;
    }

    if (el.style.fontStyle === "italic") {
      marks.italic = true;
    }

    return marks;
  },
};

const DEFAULT_BLOCK_ELEMENTS =
  /^(address|blockquote|body|center|dir|div|dl|fieldset|form|h[1-6]|hr|isindex|menu|noframes|noscript|ol|p|pre|table|ul|dd|dt|frameset|li|tbody|tfoot|thead|html)$/i;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const diveTextStyle = (attrs: any, children: any) =>
  typeof children === "string"
    ? jsx("text", attrs, children)
    : // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children.map((child: any) => {
        if (Element.isElement(child)) {
          return { ...child, children: diveTextStyle(attrs, child.children) };
        }

        if (Text.isText(child)) {
          return { ...child, ...attrs };
        }

        return jsx("text", attrs, child);
      });

const removeEmptyChildrenElements = (nodeList: Descendant[]): void => {
  nodeList.forEach(node => {
    if (Element.isElement(node)) {
      const invalidChildren = node.children.filter(
        (child): boolean => Element.isElement(child) && child.children.length === 0
      );

      invalidChildren.forEach(invalid => {
        node.children.splice(node.children.indexOf(invalid), 1);
      });

      removeEmptyChildrenElements(node.children);
    }

    if (Element.isElement(node) && node.children.length === 0) {
      node.children.push({ text: "" });
    }
  });
};

const onlyTextNodes = (children: Descendant[] | string): boolean =>
  typeof children === "string" || children.every(child => typeof child === "string" || Text.isText(child));

const processText = (textContent: string, preservingNewlines: boolean): string => {
  if (!preservingNewlines) {
    const preserveLastNewline = textContent.endsWith("\n");
    let processed = textContent;
    while (processed.includes("\n")) {
      processed = processed.replace("\n", "");
    }

    return `${processed}${preserveLastNewline ? "\n" : ""}`;
  }

  return textContent;
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const deserialize = (editor: any, el: any) => {
  // nodeType = 3 is just text.
  if (el.nodeType === 3 && el.textContent !== "\n") {
    const preservingNewlines = ["pre", "pre-line", "pre-wrap"].includes(
      (el.parentElement as HTMLElement).style.whiteSpace
    );

    return processText(el.textContent, preservingNewlines);
  }

  // nodeType = 1 is some kind of tag. There are other node types like comments but we are not interested in them at all.
  if (el.nodeType !== 1) {
    return null;
  }

  if (el.nodeName === "SCRIPT" || el.nodeName === "STYLE") {
    return null;
  }

  // We just break on every BR except when it's BR inside </p> as the last node (html element or text node).
  if (el.nodeName === "BR") {
    if (!el.nextSibling) {
      return "";
    }

    return "\n";
  }

  // COMPAT: Slack is sending such weird delimiters.
  if (el.nodeName === "SPAN" && (el.getAttribute("class") || "") === "c-mrkdwn__br") {
    return "\n";
  }

  if (el.style.display === "none") {
    return null;
  }

  const { nodeName } = el;
  const isBlockElement =
    DEFAULT_BLOCK_ELEMENTS.test(nodeName) ||
    (el.style.display === "block" && !["left", "right"].includes(el.style.float));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const children: any[] = flatten(Array.from(el.childNodes).map(child => deserialize(editor, child))).filter(
    child => child !== null
  );

  if (el && nodeName === "A" && el.getAttribute("href") && normalizeBrowserLink(el.getAttribute("href").success)) {
    trackEngagement("Editor: Link added", editor.eventPlace, { source: "copy-paste" });
  }

  if (el.nodeName === "PRE") {
    if (typeof children === "string") {
      return jsx("text", { code: true }, children);
    }
    const processedChildren = diveTextStyle({ code: true }, children);

    if (
      processedChildren.length > 0 &&
      editor.isInline(processedChildren[processedChildren.length - 1]) &&
      isBlockElement
    ) {
      processedChildren[processedChildren.length - 1].break = true;
    }

    return processedChildren;
  }

  if (["TD", "TH"].includes(el.nodeName) && onlyTextNodes(children)) {
    return typeof children === "string" ? `${children}; ` : [...children, "; "];
  }

  if (el.nodeName === "TR" && onlyTextNodes(children)) {
    let result = null;

    // @ts-expect-error TS(2339)
    if (typeof children === "string" && children.endsWith("; ")) {
      // @ts-expect-error TS(2339)
      result = jsx("element", { type: "paragraph" }, [jsx("text", {}, children.substr(0, children.length - 2))]);
    }
    if (children[children.length - 1] === "; ") {
      result = jsx("element", { type: "paragraph" }, children.slice(0, children.length - 1));
    }

    result = jsx("element", { type: "paragraph" }, children);
    return result;
  }

  if (el.nodeName === "BODY") {
    return jsx("fragment", {}, children);
  }

  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  if (Object.keys(ELEMENT_TAGS).includes(nodeName) && ELEMENT_TAGS[nodeName]) {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const attrs = ELEMENT_TAGS[nodeName](el);

    if (attrs.type === undefined) {
      return children;
    }

    const resultNode = jsx("element", attrs, nodeName === "IMG" ? jsx("text", {}, "") : children);

    // In case we have a paragraph with only one newline it is propably because author wanted to show empty paragraph.
    // We show empty paragraphs without issues, so we will just strip it.
    if (nodeName === "P") {
      if (Node.string(resultNode) === "\n") {
        return jsx("element", attrs, [jsx("text", {}, "")]);
      }
      return resultNode;
    }

    return jsx("element", attrs, nodeName === "IMG" ? jsx("text", {}, "") : children);
  }

  if (TEXT_TAGS[nodeName]) {
    if (el.textContent !== "") {
      const attrs = TEXT_TAGS[nodeName](el);
      const hasElement = children.some(Element.isElement);

      if (hasElement) {
        if (children.length === 1) {
          const processedChildren = diveTextStyle(attrs, children[0].children);
          if (editor.isInline(processedChildren[processedChildren.length - 1]) && isBlockElement) {
            processedChildren[processedChildren.length - 1].break = true;
          }
          return processedChildren;
        }

        if (children.length > 0 && editor.isInline(children[children.length - 1]) && isBlockElement) {
          children[children.length - 1].break = true;
        }

        return children;
      }

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = children.map((child: any) => jsx("text", attrs, child));
      if (result.length > 0 && isBlockElement) {
        result[result.length - 1].break = true;
      }

      return result;
    }

    return null;
  }

  if (children.length > 0 && editor.isInline(children[children.length - 1]) && isBlockElement) {
    children[children.length - 1].break = true;
  }

  return children;
};

type BreakableNode = Node & { break?: boolean };

export function withHtmlCopyPaste(editor: Editor): Editor {
  const { insertData, normalizeNode } = editor;
  editor.normalizeNode = ([node, path]): void => {
    const editedNode: BreakableNode = node;

    if (editedNode.break !== undefined) {
      Transforms.unsetNodes(editor, "break", { at: path });
      return;
    }

    normalizeNode([node, path]);
  };

  editor.insertData = (data: DataTransfer): void => {
    if (!data) {
      return;
    }

    const html = data.getData("text/html") as string;
    const text = data.getData("text/plain") as string;

    if (html) {
      const parser = new DOMParser().parseFromString(html, "text/html");
      const fragment = deserialize(editor, parser.body);

      if (JSON.stringify(fragment).indexOf('type":"p"') !== -1) {
        Sentry.withScope(scope => {
          scope.setExtra("slateHTMLpayload", { html });
        });
      }

      const cleanedFragment = dropWhile(
        fragment.reverse(),
        (trailingNode: Node) => Text.isText(trailingNode) && Node.string(trailingNode).trim() === ""
      ).reverse();

      let textWrapStart: number | null = null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let wrappedFragment: any = [];
      cleanedFragment.forEach((fragmentNode, index) => {
        const breakableFragmentNode: BreakableNode = fragmentNode;

        const isElement = Element.isElement(fragmentNode);
        const isText = Text.isText(fragmentNode);
        const isInline = isElement && editor.isInline(fragmentNode as Element);
        const isBreakable = isText || isInline;

        if (isBreakable && textWrapStart === null) {
          textWrapStart = index;
        }

        if (
          (isElement && !isInline) ||
          (isBreakable && breakableFragmentNode.break) ||
          index === cleanedFragment.length - 1
        ) {
          if (textWrapStart !== null) {
            const nodesToWrap = cleanedFragment.slice(
              textWrapStart,
              breakableFragmentNode.break || (isElement && isInline) ? index + 1 : index
            );
            if (
              Text.isText(nodesToWrap[nodesToWrap.length - 1]) &&
              ((nodesToWrap[nodesToWrap.length - 1] as RichEditorText).text as string).endsWith("\n")
            ) {
              (nodesToWrap[nodesToWrap.length - 1] as RichEditorText).text = (
                (nodesToWrap[nodesToWrap.length - 1] as RichEditorText).text as string
              ).replace(/\n$/, "");
            }
            let newNode = { type: "paragraph", children: nodesToWrap };
            if (nodesToWrap.length === 1 && Text.isText(nodesToWrap[0]) && nodesToWrap[0].text === "\n") {
              newNode = { type: "paragraph", children: [{ text: "" }] };
            }

            wrappedFragment = [...wrappedFragment, newNode];
            textWrapStart = null;
          }

          if (!isBreakable) {
            wrappedFragment.push(fragmentNode);
          }

          if (index === cleanedFragment.length - 1 && isText) {
            wrappedFragment.push({ type: "paragraph", children: [fragmentNode] });
          }
        }
      });

      if (
        wrappedFragment.length > 0 &&
        wrappedFragment[0].type === "paragraph" &&
        wrappedFragment[0].children.length === 0
      ) {
        wrappedFragment.shift();
      }

      if (
        wrappedFragment.length &&
        BLOCK_FORMATS.filter(format => !ONELINE_FORMATS.includes(format)).includes(
          wrappedFragment[wrappedFragment.length - 1].type
        )
      ) {
        // Add an empty paragraph in case of non-paragraph last item.
        wrappedFragment.push({ type: "paragraph", children: [{ text: "" }] });
      }

      removeEmptyChildrenElements(wrappedFragment);
      Transforms.insertFragment(editor, wrappedFragment, { voids: true });

      return;
    }

    if (text) {
      const paragraphs: Descendant[] = text.split("\n").map(line => {
        const urlsInside = line.split(/\s+/).filter(word => isUrl(word) && normalizeBrowserLink(word).success);
        const lineSplit = [];
        let lastStart = 0;

        urlsInside.forEach(url => {
          const endIndex = line.indexOf(url, lastStart);
          if (endIndex !== -1) {
            const partBefore = line.slice(lastStart, endIndex);
            const urlPart = line.slice(endIndex, endIndex + url.length);
            lineSplit.push({ text: partBefore });
            lineSplit.push({ type: "link", url, children: [{ text: urlPart }] });
            lastStart = endIndex + url.length;
          }
        });

        if (lastStart < line.length) {
          lineSplit.push({ text: line.slice(lastStart) });
        }
        return { type: "paragraph", children: lineSplit };
      });

      removeEmptyChildrenElements(paragraphs);
      Transforms.insertFragment(
        editor,
        paragraphs.filter(paragraph => Element.isElement(paragraph))
      );

      return;
    }

    if (data) {
      insertData(data);
    }
  };

  return editor;
}

const PluginSpec: import("./index").PluginSpec = [withErrorContext(withHtmlCopyPaste), {}];

export default PluginSpec;
